import type { z } from 'zod';
import { MAX_FILE_BYTES, type ImageType } from './files.ts';

// Endpoint contracts (design §7.3, ADR 0001). A contract is defined once in shared
// code; the server implements it with `implement()` and the browser calls it with
// `api()`. Types flow from the zod schemas, so there is no code generation.

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A declared permission, such as `bank.view`: `<module>.<action>`. */
export type PermissionString = `${string}.${string}`;

/**
 * What a caller needs: nothing, a login, any member of the guild through a Discord
 * interaction (`discord`, never from the web: ADR 0007), or a declared permission
 * (design §6.2).
 */
export type Permission = 'public' | 'authenticated' | 'discord' | PermissionString;

type Schema = z.ZodType;

/** An endpoint whose request body is one file's bytes (plan 3D). */
export interface UploadSpec {
  /** The largest body, in bytes. Anything over it is a 413. */
  maxBytes: number;
}

/** What a handler receives for an upload. `mime` is sniffed from the bytes and `name` is safe to store. */
export interface UploadedFile {
  bytes: Uint8Array;
  mime: ImageType;
  name: string;
  sha256: string;
}

/** What `api()` sends for an upload: a Blob, or a File to give it a name. */
export interface UploadBody {
  readonly type: string;
  readonly size: number;
}

export interface EndpointDefinition<
  P extends Schema | undefined,
  Q extends Schema | undefined,
  B extends Schema | undefined,
  R extends Schema,
  U extends UploadSpec | undefined = undefined,
> {
  method: HttpMethod;
  /** The path under /api, starting with the module name: `/bank/entries/:id`. */
  path: `/${string}`;
  /** Required. A contract without one doesn't compile. */
  permission: Permission;
  params?: P;
  query?: Q;
  body?: B;
  /** The response schema, shaped for this endpoint's audience. Undeclared fields are stripped. */
  response: R;
  /** Requires a login within the last 12 hours (design §6.1, D24). */
  recentLogin?: boolean;
  /** Whether an Idempotency-Key header is accepted or required (design §6.12). */
  idempotencyKey?: 'optional' | 'required';
  /**
   * This endpoint's own per-user limit, instead of the shared budget every change
   * draws on (60 a minute). Only for endpoints that change data.
   */
  rateLimit?: { max: number; windowMs?: number };
  /**
   * Takes a raw image as the body, so `body` can't be declared. Needs its own
   * `rateLimit`, and the handler receives `file`. Must be a POST.
   */
  upload?: U;
  /**
   * Whether a call writes a request audit entry (default true; only changes are
   * audited). Off only for changes to the caller's own transient state, such as live
   * subscriptions, which would flood the trail.
   */
  audit?: boolean;
}

const CONTRACT: unique symbol = Symbol.for('itc.contract');

export interface Endpoint<
  P extends Schema | undefined = Schema | undefined,
  Q extends Schema | undefined = Schema | undefined,
  B extends Schema | undefined = Schema | undefined,
  R extends Schema = Schema,
  U extends UploadSpec | undefined = UploadSpec | undefined,
> {
  readonly [CONTRACT]: true;
  readonly method: HttpMethod;
  readonly path: `/${string}`;
  readonly permission: Permission;
  readonly params: P;
  readonly query: Q;
  readonly body: B;
  readonly response: R;
  readonly recentLogin: boolean;
  readonly idempotencyKey: 'none' | 'optional' | 'required';
  readonly rateLimit: { readonly max: number; readonly windowMs: number } | null;
  readonly upload: U extends UploadSpec ? Readonly<UploadSpec> : null;
  readonly audit: boolean;
}

export type AnyEndpoint = Endpoint;

export function defineEndpoint<
  R extends Schema,
  P extends Schema | undefined = undefined,
  Q extends Schema | undefined = undefined,
  B extends Schema | undefined = undefined,
  U extends UploadSpec | undefined = undefined,
>(definition: EndpointDefinition<P, Q, B, R, U>): Endpoint<P, Q, B, R, U> {
  // The types already require these; check at runtime too, for JS callers and casts.
  const permission: unknown = definition.permission;
  if (typeof permission !== 'string' || !/^(public|authenticated|discord|[a-z][a-z0-9]*\.[a-z0-9_.]+)$/.test(permission)) {
    throw new Error(`Endpoint ${definition.method} ${definition.path} must declare a valid permission`);
  }
  // A Discord caller has no login to be recent.
  if (permission === 'discord' && definition.recentLogin === true) throw new Error(`Endpoint ${definition.method} ${definition.path} is for Discord, so it can't require a recent login`);
  if (!definition.path.startsWith('/')) throw new Error(`Endpoint path must start with "/": ${definition.path}`);
  const { rateLimit } = definition;
  if (rateLimit !== undefined) {
    const valid = Number.isInteger(rateLimit.max) && rateLimit.max > 0 && (rateLimit.windowMs === undefined || (Number.isInteger(rateLimit.windowMs) && rateLimit.windowMs > 0));
    if (!valid || definition.method === 'GET') throw new Error(`Endpoint ${definition.method} ${definition.path} has an invalid rateLimit`);
  }
  const { upload } = definition;
  if (upload !== undefined) {
    const where = `Endpoint ${definition.method} ${definition.path}`;
    if (!Number.isInteger(upload.maxBytes) || upload.maxBytes <= 0 || upload.maxBytes > MAX_FILE_BYTES) throw new Error(`${where} has an invalid upload limit`);
    if (definition.method !== 'POST') throw new Error(`${where} takes an upload, so it must be a POST`);
    if (definition.body !== undefined) throw new Error(`${where} takes an upload, so it can't declare a body`);
    if (rateLimit === undefined) throw new Error(`${where} takes an upload, so it needs its own rateLimit`);
  }
  return Object.freeze({
    [CONTRACT]: true as const,
    method: definition.method,
    path: definition.path,
    permission: definition.permission,
    params: definition.params as P,
    query: definition.query as Q,
    body: definition.body as B,
    response: definition.response,
    recentLogin: definition.recentLogin ?? false,
    idempotencyKey: definition.idempotencyKey ?? 'none',
    rateLimit: rateLimit === undefined ? null : Object.freeze({ max: rateLimit.max, windowMs: rateLimit.windowMs ?? 60_000 }),
    upload: (upload === undefined ? null : Object.freeze({ maxBytes: upload.maxBytes })) as U extends UploadSpec ? Readonly<UploadSpec> : null,
    audit: definition.audit ?? true,
  });
}

export function isEndpoint(value: unknown): value is AnyEndpoint {
  return typeof value === 'object' && value !== null && CONTRACT in value;
}

type Part<S, Key extends string, Kind extends 'in' | 'out'> = S extends Schema
  ? { [K in Key]: Kind extends 'in' ? z.input<S> : z.output<S> }
  : unknown;

type FilePart<E extends AnyEndpoint, File> = E['upload'] extends null ? unknown : { file: File };

/** What a caller passes: the schemas' inputs, only for the parts the contract declares. */
export type EndpointInput<E extends AnyEndpoint> = Part<E['params'], 'params', 'in'> &
  Part<E['query'], 'query', 'in'> &
  Part<E['body'], 'body', 'in'> &
  FilePart<E, UploadBody>;

/** What a handler receives: the parsed outputs of the declared parts. */
export type EndpointParsedInput<E extends AnyEndpoint> = Part<E['params'], 'params', 'out'> &
  Part<E['query'], 'query', 'out'> &
  Part<E['body'], 'body', 'out'> &
  FilePart<E, UploadedFile>;

/** What a handler returns, before the response schema strips and transforms it. */
export type EndpointResult<E extends AnyEndpoint> = z.input<E['response']>;

/** What a caller receives. */
export type EndpointResponse<E extends AnyEndpoint> = z.output<E['response']>;

/** Fills `:name` segments of a contract path. Values are URI-encoded. */
export function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => {
    const value = params?.[name];
    if (typeof value !== 'string' && typeof value !== 'number') {
      throw new Error(`Path parameter "${name}" for ${path} must be a string or number`);
    }
    return encodeURIComponent(value);
  });
}
